//@ts-strict-ignore
import React, { useContext, useEffect } from 'react';
import Dropdown from '../../components/connectedForm/Dropdown';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import { default as Form } from '../../components/connectedForm';
import { useValueState } from '../../utils/hooks';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { BluePill } from '../DropdownEditor/CustomOptions';
import { IAbstractContact } from '../../graphql/schema/p4types';
import { IDropdownOptionRenderer } from '../../@types/entityTypes';

export const ContactOptionRenderer: IDropdownOptionRenderer = ({ option }) => {
  const email = option.email || option.id;

  if (option.name?.fullName && option.name.fullName !== email) {
    if (option.id === 'delivery') {
      return (
        <>
          {option.name.fullName} - {email}
          <BluePill style={{ marginLeft: '4px', float: 'right' }}>Delivery</BluePill>
        </>
      );
    }
    return (
      <span>
        {option.name.fullName} - {email}
      </span>
    );
  }
  return <span>{email}</span>;
};

//Style to apply to option labels for "preferred" contacts (I.e. Contacts tagged to receive the document type being sent).
export const PREFERRED_CONTACT_STYLE = { borderRight: '7px solid #4caf50' };

const useEmailState = (initialValue) => {
  const [email, setEmail] = useValueState(initialValue);

  const onSetEmail = (input) => {
    input?.forEach((value) => {
      if (!value.email) {
        //Custom additions need to be copied over to the email field.
        //(They are added against 'id' due to it being the unique field on the dropdown.
        value.email = value.id;
      }
    });
    setEmail(input);
  };

  return [email, onSetEmail];
};

/**
 * Enhanced version of 'EmailEditor' that lists all contact email addresses & tags the 'default' ones with a colour highlight.
 */
const ContactEmailEditor = ({
  allowSubject,
  allowEmailCc,
  allowEmailTo,
  defaultEmailConfig,
  commonRecord,
  onUpdate,
}) => {
  const baseContactsList = commonRecord?.contacts?.filter((contact) => !!contact.email) || [];
  if (commonRecord?.email) {
    baseContactsList.push({ ...commonRecord, id: 'customer' }); //Changes the id to 'customer' to avoid conflicts with contact id's - Only the email address will eventually be used from the items in this list anyway  }
  }

  const connectedForm = useContext(ConnectedFormContext);

  const deliveryEmail: string | null = connectedForm.values?.deliveryEmail;
  const deliveryName: string | null = connectedForm.values?.customer?.name;
  const deliveryData = { email: deliveryEmail, name: { fullName: deliveryName }, id: 'delivery' } as IAbstractContact;

  if (!!deliveryEmail) {
    baseContactsList.unshift(deliveryData);
  }

  const contactsList = baseContactsList.map((contact) => ({
    ...contact,
    style: defaultEmailConfig.emailTo.find((entry) => entry.email === contact.email) ? PREFERRED_CONTACT_STYLE : null,
  }));

  const emailFrom = defaultEmailConfig.emailFrom;

  const [emailTo, setEmailTo] = useEmailState(
    defaultEmailConfig.emailTo?.map((entry) => ({ ...entry, style: PREFERRED_CONTACT_STYLE })) || []
  );
  const [emailCc, setEmailCc] = useEmailState(
    defaultEmailConfig.emailCc?.map((entry) => ({ ...entry, style: PREFERRED_CONTACT_STYLE })) || []
  );
  const [emailSubject, setEmailSubject] = useValueState(defaultEmailConfig.emailSubject);

  useEffect(() => {
    onUpdate({
      emailFrom,
      emailTo,
      emailCc,
      emailSubject,
    });
  }, [emailTo, emailCc, emailSubject]);

  /**
   * Convert a list of emails into a format that the multi-select dropdown can handle.
   * @param emailTo - An array or comma seperated string of email addresses.
   * @returns {*} An array in the format [{label, value}, {label, value}]
   */
  return (
    <div>
      <Form.Legend label={'Email Configuration'} styles={{ root: { marginRight: 0 } }} />
      {defaultEmailConfig.emailFrom ? (
        <BlurTextField label="From" value={defaultEmailConfig.emailFrom} readOnly={true} />
      ) : null}
      {allowEmailTo ? (
        <Dropdown
          testId="email-to-input"
          dataid="emailTo"
          options={contactsList}
          label={'To'}
          multi={true}
          value={emailTo}
          onChange={setEmailTo}
          creatable={true}
          rawLabel={true}
          labelKey={'name.fullName'}
          valueKey={'id'}
          valueRenderer={ContactOptionRenderer}
          optionRenderer={ContactOptionRenderer}
        />
      ) : null}
      {allowEmailCc ? (
        <Dropdown
          testId="email-cc-input"
          dataid="emailCc"
          options={contactsList}
          label={'Cc'}
          multi={true}
          value={emailCc}
          onChange={setEmailCc}
          creatable={true}
          rawLabel={true}
          labelKey={'name.fullName'}
          valueKey={'id'}
          valueRenderer={ContactOptionRenderer}
          optionRenderer={ContactOptionRenderer}
        />
      ) : null}
      {allowSubject ? (
        <BlurTextField label="Subject" value={emailSubject} onChange={setEmailSubject} enableOnChange />
      ) : null}
    </div>
  );
};
ContactEmailEditor.defaultProps = {
  allowEmailTo: false,
  allowEmailCc: false,
  allowSubject: true,
  emailTo: [],
  emailCc: [],
  emailSubject: '',
};

export default ContactEmailEditor;
